const RAW_API_BASE_URL = String(process.env.REACT_APP_ORDERING_API_BASE_URL || '').trim();
const FETCH_TIMEOUT_MS = 15000;
const REDIRECTING_API_ORIGIN_OVERRIDES = {
  'https://komiskenkey.com': 'https://www.komiskenkey.com',
};

function stripTrailingSlash(value = '') {
  return String(value || '').replace(/\/$/, '');
}

function isLoopbackHostname(hostname = '') {
  return ['localhost', '127.0.0.1', '::1'].includes(String(hostname || '').toLowerCase());
}

function getBrowserOrigin() {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }

  return stripTrailingSlash(window.location.origin);
}

function normalizeBaseUrl(candidate = '') {
  const trimmed = stripTrailingSlash(candidate);
  if (!trimmed) return '';

  try {
    const origin = new URL(trimmed).origin;
    return REDIRECTING_API_ORIGIN_OVERRIDES[origin] || origin;
  } catch {
    return '';
  }
}

function shouldIgnoreConfiguredBase(baseUrl) {
  if (!baseUrl) return false;

  const browserOrigin = getBrowserOrigin();
  if (!browserOrigin) return false;

  try {
    const configuredUrl = new URL(baseUrl);
    const currentUrl = new URL(browserOrigin);

    return !isLoopbackHostname(currentUrl.hostname) && isLoopbackHostname(configuredUrl.hostname);
  } catch {
    return false;
  }
}

function getRequestCandidates(path) {
  const candidates = [];
  const configuredBase = normalizeBaseUrl(RAW_API_BASE_URL);
  const browserOrigin = getBrowserOrigin();
  const canUseConfiguredBase = configuredBase && !shouldIgnoreConfiguredBase(configuredBase);

  if (canUseConfiguredBase) {
    candidates.push(`${configuredBase}${path}`);
  }

  if (!canUseConfiguredBase && browserOrigin) {
    candidates.push(`${browserOrigin}${path}`);
  }

  if (!canUseConfiguredBase) {
    candidates.push(path);
  }

  return Array.from(new Set(candidates));
}

function buildConnectivityHint() {
  const configuredBase = normalizeBaseUrl(RAW_API_BASE_URL);
  if (!configuredBase) {
    return 'Check that the ordering backend is running and reachable from this POS app.';
  }

  try {
    const configuredUrl = new URL(configuredBase);

    if (isLoopbackHostname(configuredUrl.hostname)) {
      return 'The POS backend is configured for a local URL. Start the ordering backend there and run the POS app on a different port, or change REACT_APP_ORDERING_API_BASE_URL to the deployed ordering app.';
    }
  } catch {
    return 'Check that REACT_APP_ORDERING_API_BASE_URL points to a valid ordering backend.';
  }

  return `Check that the ordering backend at ${configuredBase} is reachable from this POS app.`;
}

async function fetchJsonWithTimeout(url, options, fallbackMessage) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    // Reading the body shares the same signal/timer, so a server that sends
    // headers then stalls the body still trips the timeout instead of hanging.
    return await readJson(response, fallbackMessage);
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error(`Request timed out. ${buildConnectivityHint()}`);
      timeoutError.status = 408;
      throw timeoutError;
    }

    // HTTP errors from readJson already carry a status — propagate them as-is.
    if (error.status !== undefined) {
      throw error;
    }

    const networkError = new Error(`Could not reach the POS backend. ${buildConnectivityHint()}`);
    networkError.status = 0;
    networkError.cause = error;
    throw networkError;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function readJson(response, fallbackMessage) {
  const rawBody = await response.text().catch((error) => {
    // An abort during body read must surface (→ 408), not be masked as an empty body.
    if (error?.name === 'AbortError') throw error;
    return '';
  });
  let data = {};

  if (rawBody) {
    try {
      data = JSON.parse(rawBody);
    } catch {
      data = { message: rawBody };
    }
  }

  if (!response.ok) {
    const message = data.message || data.error || data.details || fallbackMessage;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    error.body = rawBody;
    error.url = response.url;
    throw error;
  }

  return data;
}

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
// Errors that prove the request was never processed (no such route/method), so
// retrying against the next candidate URL is safe for ANY method.
const NOT_PROCESSED_STATUSES = [404, 405];
// Ambiguous errors — the server may have already committed the request. Only
// retry these for idempotent methods; replaying a POST could double-write
// (duplicate order/payment).
const AMBIGUOUS_RETRY_STATUSES = [408, 502, 503, 504];

function isIdempotentMethod(method) {
  return IDEMPOTENT_METHODS.has(String(method || 'GET').toUpperCase());
}

function shouldTryNextCandidate(error, isLastCandidate, method) {
  if (isLastCandidate) return false;

  const idempotent = isIdempotentMethod(method);
  // Network error with no response status: the request never got a definitive
  // answer, so only an idempotent method may safely move on.
  if (!error?.status) return idempotent;

  if (NOT_PROCESSED_STATUSES.includes(error.status)) return true;
  return idempotent && AMBIGUOUS_RETRY_STATUSES.includes(error.status);
}

async function requestJson(path, options, fallbackMessage) {
  const candidates = getRequestCandidates(path);
  let lastError = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const url = candidates[index];
    const isLastCandidate = index === candidates.length - 1;

    try {
      return await fetchJsonWithTimeout(url, options, fallbackMessage);
    } catch (error) {
      lastError = error;
      if (!shouldTryNextCandidate(error, isLastCandidate, options?.method)) {
        throw error;
      }
    }
  }

  throw lastError || new Error(fallbackMessage);
}

function buildHeaders(accessToken, includeJson = false) {
  const headers = {};

  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

function unwrapPayload(value) {
  let current = value;

  while (
    current &&
    typeof current === 'object' &&
    !Array.isArray(current) &&
    Object.prototype.hasOwnProperty.call(current, 'data') &&
    current.data !== current
  ) {
    current = current.data;
  }

  return current;
}

export async function createPosSession(username, password) {
  return requestJson('/api/pos/session', {
    method: 'POST',
    headers: buildHeaders(null, true),
    body: JSON.stringify({ username, password }),
  }, 'Could not sign in to the POS backend.');
}

export async function getPosResource(resource, accessToken, params = {}) {
  const searchParams = new URLSearchParams({ resource });

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, value);
    }
  });

  const data = await requestJson(`/api/pos?${searchParams.toString()}`, {
    method: 'GET',
    headers: buildHeaders(accessToken, false),
  }, `Could not load ${resource}.`);

  return unwrapPayload(data);
}

export async function mutatePosResource(resource, action, payload, accessToken) {
  const data = await requestJson('/api/pos', {
    method: 'POST',
    headers: buildHeaders(accessToken, true),
    body: JSON.stringify({ resource, action, payload }),
  }, `Could not update ${resource}.`);

  return unwrapPayload(data);
}

// Register (upsert) this device's Web Push subscription with the ordering
// backend so it can send admin notifications. The backend keys the row by the
// authenticated POS user (from the Bearer token) and records their role, so it
// never trusts a client-supplied identity.
export async function savePushSubscription(subscription, accessToken) {
  const json = typeof subscription?.toJSON === 'function' ? subscription.toJSON() : subscription || {};
  return requestJson('/api/push/subscribe', {
    method: 'POST',
    headers: buildHeaders(accessToken, true),
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys?.p256dh || '',
        auth: json.keys?.auth || '',
      },
    }),
  }, 'Could not register for notifications.');
}

// Remove a Web Push subscription server-side (admin turned notifications off, or
// the browser endpoint was rotated). POST — not DELETE — so the endpoint travels
// in the body and we reuse the same retry/timeout path as every other call.
export async function deletePushSubscription(endpoint, accessToken) {
  return requestJson('/api/push/unsubscribe', {
    method: 'POST',
    headers: buildHeaders(accessToken, true),
    body: JSON.stringify({ endpoint }),
  }, 'Could not unregister notifications.');
}

// Forwards a chat turn to the AI backend (/api/ai on the ordering backend),
// which holds the Anthropic API key server-side and calls Claude. The browser
// only ever sends conversation messages + tool definitions + a system prompt —
// never the API key, never restaurant data. Returns Claude's raw Messages API
// response ({ role, content, stop_reason, ... }).
export async function postAiChat(body, accessToken) {
  return requestJson('/api/ai', {
    method: 'POST',
    headers: buildHeaders(accessToken, true),
    body: JSON.stringify(body),
  }, 'The AI assistant is unavailable right now.');
}

// Concatenate the text blocks of a Messages-API content array (used when a
// streaming request falls back to a plain JSON response).
function extractTextBlocks(content) {
  if (!Array.isArray(content)) return '';
  return content.filter((b) => b?.type === 'text').map((b) => b.text).join('\n');
}

// Assemble a complete Messages-API response from a stream of Anthropic SSE event
// objects, emitting accumulated assistant text through onTextDelta as it arrives.
// Pure (no fetch/DOM) so it can be unit-tested with a plain async iterable.
// `events` yields the parsed `data:` object of each SSE event.
export async function assembleMessageFromEvents(events, onTextDelta) {
  const blocks = [];
  let role = 'assistant';
  let stopReason = null;
  let accumText = '';
  const emit = (delta) => {
    if (!delta) return;
    accumText += delta;
    if (typeof onTextDelta === 'function') onTextDelta(accumText);
  };

  for await (const ev of events) {
    switch (ev?.type) {
      case 'message_start':
        role = ev.message?.role || 'assistant';
        break;
      case 'content_block_start': {
        const cb = ev.content_block || {};
        blocks[ev.index] = cb.type === 'tool_use'
          ? { type: 'tool_use', id: cb.id, name: cb.name, input: {}, _json: '' }
          : { type: 'text', text: '' };
        if (cb.type === 'text' && cb.text) { blocks[ev.index].text = cb.text; emit(cb.text); }
        break;
      }
      case 'content_block_delta': {
        const block = blocks[ev.index];
        const delta = ev.delta || {};
        if (!block) break;
        if (delta.type === 'text_delta') { block.text += delta.text || ''; emit(delta.text); }
        else if (delta.type === 'input_json_delta') { block._json += delta.partial_json || ''; }
        break;
      }
      case 'content_block_stop': {
        const block = blocks[ev.index];
        if (block && block.type === 'tool_use') {
          try { block.input = block._json ? JSON.parse(block._json) : {}; } catch { block.input = {}; }
          delete block._json;
        }
        break;
      }
      case 'message_delta':
        if (ev.delta?.stop_reason) stopReason = ev.delta.stop_reason;
        break;
      default:
        break;
    }
  }

  return { role, content: blocks.filter(Boolean), stop_reason: stopReason };
}

// Read an SSE byte stream and yield each event's parsed JSON `data`. Stops early
// (and lets the reader cancel) if isCancelled() flips true.
async function* readSseEvents(body, isCancelled) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      if (isCancelled && isCancelled()) return;
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const dataStr = rawEvent
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('');
        if (!dataStr || dataStr === '[DONE]') continue;
        try { yield JSON.parse(dataStr); } catch { /* keepalive / partial — skip */ }
      }
    }
  } finally {
    try { reader.cancel(); } catch { /* already closed */ }
  }
}

async function streamAiOnce(url, body, accessToken, onTextDelta, isCancelled) {
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(accessToken, true),
      body: JSON.stringify({ ...body, stream: true }),
    });
  } catch (error) {
    const networkError = new Error(`Could not reach the AI service. ${buildConnectivityHint()}`);
    networkError.status = 0;
    networkError.cause = error;
    throw networkError;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data?.error || data?.message || 'The AI assistant is unavailable right now.');
    error.status = response.status;
    throw error;
  }

  // Backend didn't stream (older deploy, or it chose JSON) — adapt the full
  // response so the caller behaves identically either way.
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream') || !response.body) {
    const data = await response.json().catch(() => ({}));
    const text = extractTextBlocks(data?.content);
    if (text && typeof onTextDelta === 'function') onTextDelta(text);
    return data;
  }

  return assembleMessageFromEvents(readSseEvents(response.body, isCancelled), onTextDelta);
}

// Streaming twin of postAiChat: requests Server-Sent Events from /api/ai and
// surfaces assistant text incrementally via onTextDelta. Resolves to the same
// assembled Messages-API response shape as postAiChat, so the agent loop's tool
// handling is unchanged. Transparently falls back to a plain JSON response when
// the backend does not stream.
export async function postAiChatStream(body, accessToken, { onTextDelta, isCancelled } = {}) {
  const candidates = getRequestCandidates('/api/ai');
  let lastError = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const isLastCandidate = index === candidates.length - 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      return await streamAiOnce(candidates[index], body, accessToken, onTextDelta, isCancelled);
    } catch (error) {
      lastError = error;
      if (!shouldTryNextCandidate(error, isLastCandidate, 'POST')) throw error;
    }
  }

  throw lastError || new Error('The AI assistant is unavailable right now.');
}
